import { CheckIcon } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect } from 'react'

import { ConfettiBurst } from './ConfettiBurst'

const SHOWN_MILLISECONDS = 1600

interface TrialFinishedCelebrationProps {
  isShown: boolean
  onDone: () => void
}

export const TrialFinishedCelebration = ({
  isShown,
  onDone,
}: TrialFinishedCelebrationProps) => {
  const reduceMotion = useReducedMotion() ?? false

  useEffect(() => {
    if (!isShown) return
    const timer = setTimeout(onDone, SHOWN_MILLISECONDS)
    return () => clearTimeout(timer)
  }, [isShown, onDone])

  return (
    <AnimatePresence>
      {isShown && (
        <div className="pointer-events-none fixed inset-x-0 top-[18vh] z-50 flex flex-col items-center px-4">
          {!reduceMotion && <ConfettiBurst />}
          <CelebrationCard reduceMotion={reduceMotion} onDismiss={onDone} />
        </div>
      )}
    </AnimatePresence>
  )
}

interface CelebrationCardProps {
  reduceMotion: boolean
  onDismiss: () => void
}

const CelebrationCard = ({ reduceMotion, onDismiss }: CelebrationCardProps) => (
  <motion.button
    type="button"
    onClick={onDismiss}
    className="pointer-events-auto flex cursor-pointer items-center gap-4 rounded-xl border bg-card px-6 py-5 text-left shadow-lg"
    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -8, scale: 0.97 }}
    transition={{ type: 'spring', stiffness: 320, damping: 22 }}
  >
    <SuccessMark reduceMotion={reduceMotion} />
    <span className="flex flex-col gap-0.5">
      <span className="font-semibold text-card-foreground text-lg">
        Congratulations!
      </span>
      <span className="text-muted-foreground text-sm">Your case is ready.</span>
    </span>
  </motion.button>
)

const SuccessMark = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <span className="relative flex size-11 shrink-0 items-center justify-center">
    {!reduceMotion && (
      <motion.span
        className="absolute inset-0 rounded-full bg-brand"
        initial={{ scale: 1, opacity: 0.5 }}
        animate={{ scale: 1.9, opacity: 0 }}
        transition={{ duration: 1.1, delay: 0.25, ease: 'easeOut' }}
      />
    )}
    <motion.span
      className="relative flex size-11 items-center justify-center rounded-full bg-brand text-white"
      initial={reduceMotion ? false : { scale: 0, rotate: -45 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 420, damping: 16, delay: 0.1 }}
    >
      <CheckIcon className="size-6" strokeWidth={3} />
    </motion.span>
  </span>
)
